/**
 * Empat analitik K179 (langkah 19 · 20 · 23 · 27): waste kain per WO (S7), lead time vendor (S8),
 * profitabilitas (S15, Owner saja), repeat order musiman (S9). Angka dihitung langsung dari transaksi;
 * bila datanya belum cukup, layar menyatakannya — tidak ada angka karangan.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Hint from "@/components/etams/Hint";

const TAB = [
  { kode: "waste", nama: "Waste kain per WO", url: "/analitik/waste-kain", peran: ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"] },
  { kode: "lead", nama: "Lead time vendor", url: "/analitik/lead-time-vendor", peran: ["OWNER", "KEPALA_CABANG", "ADMIN_PURCHASING", "ADMIN_GUDANG"] },
  { kode: "musim", nama: "Repeat order musiman", url: "/analitik/repeat-musiman", peran: ["OWNER", "KEPALA_CABANG", "CS"] },
  { kode: "profit", nama: "Profitabilitas", url: "/analitik/profitabilitas", peran: ["OWNER"] },
  { kode: "tujuan", nama: "Tanda TUJUAN (7 hari)", url: "/analitik/tanda-tujuan", peran: ["OWNER"] },
];
const rp = (n) => (n == null ? "—" : `Rp${Number(n).toLocaleString("id-ID")}`);

function Tabel({ kolom, baris, kosong }) {
  if (!baris?.length) return <div className="p-4 text-sm text-muted-foreground">{kosong}</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
        <tr>{kolom.map(([, j]) => <th key={j} className="px-3 py-2 text-left">{j}</th>)}</tr>
      </thead>
      <tbody>
        {baris.map((b, i) => (
          <tr key={i} className="border-t">{kolom.map(([f, j]) => <td key={j} className="px-3 py-2">{f(b)}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Analitik() {
  const { roleCode } = useAuth();
  const tersedia = TAB.filter((t) => t.peran.includes(roleCode));
  const [tab, setTab] = useState(tersedia[0]?.kode);
  const [data, setData] = useState(null);
  const aktif = tersedia.find((t) => t.kode === tab);

  const muat = useCallback(async () => {
    if (!aktif) return;
    setData(null);
    try { const r = await api.get(aktif.url); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Analitik gagal dimuat")); }
  }, [aktif]);
  useEffect(() => { muat(); }, [muat]);

  const margin = [["nilai", "Kelompok"], ["fa", "FA"], ["penjualan_idr", "Penjualan"], ["hpp_idr", "HPP"], ["margin_pct", "Margin %"]]
    .map(([k, j]) => [(b) => (k.endsWith("_idr") ? rp(b[k]) : b[k] ?? "—"), j]);

  return (
    <div className="max-w-5xl space-y-4" data-testid="analitik-page">
      <div>
        <h1 className="text-2xl font-semibold">
          Analitik{" "}
          <Hint>Dihitung langsung dari transaksi. Angka makin bermakna seiring data nyata terkumpul.</Hint>
        </h1>
      </div>
      <div className="flex flex-wrap gap-2">
        {tersedia.map((t) => (
          <Button key={t.kode} size="sm" variant={t.kode === tab ? "default" : "outline"} onClick={() => setTab(t.kode)} data-testid={`analitik-tab-${t.kode}`}>{t.nama}</Button>
        ))}
      </div>
      <Card className="overflow-x-auto">
        {!data && <div className="p-4 text-sm text-muted-foreground">Memuat…</div>}
        {data && tab === "waste" && (
          <Tabel kosong="Belum ada pengeluaran kain yang disetujui." baris={data.items} kolom={[
            [(b) => b.wo_number, "WO"], [(b) => b.kain, "Kain"], [(b) => b.lembar, "Lembar"],
            [(b) => `${b.terpakai} ${b.satuan || ""}`, "Terpakai"], [(b) => b.seharusnya ?? "—", "Seharusnya"],
            [(b) => (b.waste_pct == null ? (b.catatan || "—") : `${b.waste_pct}%`), "Waste"]]} />
        )}
        {data && tab === "lead" && (
          <Tabel kosong="Belum ada PO yang sudah diterima." baris={data.items} kolom={[
            [(b) => b.vendor, "Vendor"], [(b) => b.bahan, "Bahan"], [(b) => b.n, "Penerimaan"],
            [(b) => b.rata, "Rata-rata hari"], [(b) => `${b.min}–${b.maks}`, "Rentang"]]} />
        )}
        {data && tab === "musim" && (
          <Tabel kosong="Tidak ada customer yang pesanan terakhirnya 11–13 bulan lalu." baris={data.items} kolom={[
            [(b) => b.nama, "Customer"], [(b) => b.fa_terakhir, "FA terakhir"], [(b) => b.kalimat, "Pengingat"]]} />
        )}
        {data && tab === "profit" && (
          <div className="space-y-4 p-3">
            <div className="text-xs text-muted-foreground">{data.dari} s.d. {data.sampai} · {data.fa} FA · {data.catatan}</div>
            <div><div className="font-medium">Per cabang</div><Tabel kolom={margin} baris={data.per_cabang} kosong="Belum ada FA." /></div>
            <div><div className="font-medium">Per paket</div><Tabel kolom={margin} baris={data.per_paket} kosong="Belum ada FA." /></div>
            <div><div className="font-medium">Per CS</div><Tabel kolom={margin} baris={data.per_cs} kosong="Belum ada FA." /></div>
            <div><div className="font-medium">Per customer (50 margin terendah)</div><Tabel kolom={margin} baris={data.per_customer} kosong="Belum ada FA." /></div>
          </div>
        )}
        {data && tab === "tujuan" && (
          <div className="space-y-3 p-4 text-sm" data-testid="analitik-tanda-tujuan">
            <div>
              <div className="font-medium">Tanda 1 — posisi pesanan bisa dilihat tanpa bertanya</div>
              <div>Ruang Pesanan dibuka: {data.tanda_1.ruang_pesanan_dibuka} dari {data.tanda_1.fa_berjalan} FA berjalan ({data.tanda_1.persen ?? "—"}%)</div>
              <div>Pesan WA dari customer ber-FA berjalan: {data.tanda_1.pertanyaan_wa_customer_berjalan} ({data.tanda_1.customer_bertanya} customer)</div>
            </div>
            <div>
              <div className="font-medium">Tanda 2 — telat ketahuan sebelum customer bertanya</div>
              <div>Alarm risiko telat yang lahir sebelum customer bertanya: {data.tanda_2.alarm_sebelum_customer_bertanya} dari {data.tanda_2.alarm_dinilai} ({data.tanda_2.persen ?? "—"}%)</div>
            </div>
            <div className="text-xs text-muted-foreground">{data.catatan}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
